import type * as FavCache from '@finn-no/favorite-api-browser-client';
import pulse from '@finn-no/pulse-sdk';
import { TrackerData } from '@finn-no/pulse-sdk/dist/types';
import React, { forwardRef } from 'react';
import { toast } from '@warp-ds/elements/toast';
import '@warp-ds/elements/components/toast';
import { Card, Clickable } from '@warp-ds/react';
import { i18n } from '@lingui/core';

// this will be fixed by changing to esnext moduleResolution in TS
// but i'm not changing every import in this project just to do that
// @ts-ignore
import IconLandscape56 from '@warp-ds/icons/react/landscape-56';

type FolderButtonProps = {
    adExists: boolean;
    itemType: FavCache.ItemType;
    itemId: number;
    api: FavCache.FavoriteBrowserCache;
    closeCallback: () => void;
    folder: FavCache.FavoriteListType;
    handleError: (e: string) => void;
};

const doToast = (message: string, options: { type: 'error' | 'success' }) => {
    try {
        toast(message, options);
    } catch (e) {
        console.error('Problem toasting', e);
    }
};

const FolderButton = forwardRef(
    (props: FolderButtonProps, forwardRef: React.Ref<HTMLButtonElement>) => {
        const {
            adExists,
            itemType,
            itemId,
            api,
            closeCallback,
            folder,
            handleError,
        } = props;

        async function handleToggleFavorite() {
            // clear out any potential error messages, if none exists then it's a no-op
            handleError('');

            adExists ? await unFavorite() : await addFavorite();
        }

        async function addFavorite() {
            try {
                await api.addToFolder(itemType, itemId, folder.folderid);

                const pulsePayload: TrackerData = {
                    type: 'Click',
                    intent: 'Save',
                    name: 'New favorite',
                    object: {
                        id: `favoritelist:${folder.folderid}`,
                        type: 'UIElement',
                        elementType: 'Popup',
                        name: 'Favorite List',
                        items: [
                            {
                                type: 'ClassifiedAd',
                                id:
                                    itemType === 'Hotel'
                                        ? `hotel:${itemId}`
                                        : `${itemId}`,
                                contentId: `${itemId}`,
                                name: 'n/a',
                                category: 'n/a',
                            },
                        ],
                    },
                    target: {
                        id:
                            itemType === 'Hotel'
                                ? `classified:hotel:${itemId}`
                                : `classified:${itemId}`,
                        type: 'SavedList',
                        name: 'Favorite list',
                    },
                };
                pulse.trackEvent(pulsePayload);
                doToast(
                    i18n.t({
                        id: 'favoriteAddedToast',
                        values: { folder: folder.name },
                    }),
                    {
                        type: 'success',
                    },
                );
                closeCallback();
            } catch (error) {
                console.error(error);

                handleError(
                    i18n.t({
                        id: 'favoriteAddedErrorToast',
                    }),
                );
            }
        }

        async function unFavorite() {
            try {
                await api.removeFromFolder(itemType, itemId, folder.folderid);
                const pulsePayload: TrackerData = {
                    type: 'Click',
                    intent: 'Edit',
                    name: 'New favorite',
                    object: {
                        id: `favoritelist:${folder.folderid}`,
                        type: 'UIElement',
                        elementType: 'Button',
                        name: 'Favorite List',
                        items: [
                            {
                                type: 'ClassifiedAd',
                                id:
                                    itemType === 'Hotel'
                                        ? `hotel:${itemId}`
                                        : `${itemId}`,
                                contentId: `${itemId}`,
                                name: 'n/a',
                                category: 'n/a',
                            },
                        ],
                    },
                    target: {
                        id:
                            itemType === 'Hotel'
                                ? `classified:hotel:${itemId}`
                                : `classified:${itemId}`,
                        type: 'FavoriteItem',
                        name: 'Classified Ad',
                    },
                };
                pulse.trackEvent(pulsePayload);

                doToast(
                    i18n.t({
                        id: 'favoriteRemovedToast',
                        values: { folder: folder.name },
                    }),
                    {
                        type: 'success',
                    },
                );
                closeCallback();
            } catch (error) {
                console.error(error);

                handleError(
                    i18n.t({
                        id: 'favoriteRemovedErrorToast',
                    }),
                );
            }
        }

        return (
            <Card selected={adExists}>
                <div className="grid grid-cols-4 md:grid-cols-3 flex-row">
                    {folder.image ? (
                        <img
                            style={{ aspectRatio: '1/1' }}
                            className="w-full rounded-l-8 object-cover"
                            src={folder.image}
                            alt="Description"
                        />
                    ) : (
                        <IconLandscape56 className="w-full rounded-l-8 p-24 h-full image-aspect object-cover s-icon-subtle s-bg-subtle" />
                    )}
                    <div className="col-span-2 p-16">
                        <Clickable onClick={handleToggleFavorite}>
                            <p className="text-14 text-left text-ellipsis line-clamp-2">
                                {folder.name.trim()}
                            </p>
                        </Clickable>
                    </div>
                </div>
            </Card>
        );
    },
);

export default FolderButton;
